// client/src/components/common/Navbar.js
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  ListItemButton
} from '@mui/material';

import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Translate as TranslateIcon,
  Logout as LogoutIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  Settings as SettingsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  CloudUpload as UploadIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';
import NavMenu from './NavMenu';

const Navbar = () => {
  const { t } = useTranslation();
  const { user, isAdmin, logout } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    codes: false,
    bylaws: false
  });

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleCloseUserMenu();
  };

  const handleToggleGroup = (group) => {
    setOpenGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Define navigation groups
  const navGroups = [
    {
      id: 'main',
      items: [
        { 
          text: t('dashboard'), 
          icon: <DashboardIcon />, 
          link: '/dashboard',
          roles: ['admin', 'faculty']
        }
      ]
    },
    {
      id: 'codes',
      title: t('codes'),
      icon: <CodeIcon />,
      items: [
        { 
          text: t('faculties'), 
          icon: <SchoolIcon />, 
          link: '/faculties',
          roles: ['admin', 'faculty']
        },
        { 
          text: t('users'), 
          icon: <PersonIcon />, 
          link: '/users',
          roles: ['admin']
        },
        { 
          text: t('bankAccounts'), 
          icon: <AccountBalanceIcon />, 
          link: '/bank-accounts',
          roles: ['admin']
        }
      ]
    },
    {
      id: 'bylaws',
      title: t('bylaws'),
      icon: <DescriptionIcon />,
      items: [
        { 
          text: t('paymentItems'), 
          icon: <ReceiptIcon />, 
          link: '/payment-items',
          roles: ['admin', 'faculty']
        },
        {
          text: t('billingDetails'),
          link: '/billing-details',
          icon: <ReceiptIcon />,
          roles: ['admin', 'faculty'] // Adjust based on your permissions structure
        },
        { 
          text: t('paymentBylaws'), 
          icon: <DescriptionIcon />, 
          link: '/payment-bylaws',
          roles: ['admin', 'faculty']
        }
      ]
    },
    {
      id: 'billing',
      title: t('billing'),
      icon: <PaymentsIcon />,
      items: [
        { 
          text: t('billingExecutionList'), 
          icon: <PaymentsIcon />, 
          //link: '/billing-execution-list',
          link: '/billing-receipts',
          
          roles: ['admin', 'faculty']
        },
        {
          text: t('paymentUploads'),
          icon: <UploadIcon />,
          link: '/payment-uploads',
          //allowedRoles: ['admin', 'faculty'] // Modify as needed based on your role system
          roles: ['admin', 'faculty']
        },
        // Payment reports menu item
        {
          text: t('paymentReports'),
          icon: <ReportIcon />,
          link: '/payment-reports',
          roles: ['admin', 'faculty']
        }
      ]
    }
  ];


  


  // Filter navigation items based on user role
  const filteredNavGroups = navGroups.map(group => ({
    ...group,
    items: group.items.filter(item => 
      !item.roles || item.roles.includes(user?.role)
    )
  })).filter(group => group.items.length > 0);

  const renderMenuItems = (items, mobile = false) => {
    return items.map((item) => (
      <Button
        key={item.text}
        component={RouterLink}
        to={item.link}
        sx={{ 
          my: 2, 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center' 
        }}
        startIcon={item.icon}
      >
        {item.text}
      </Button>
    ));
  };

  const renderMobileDrawerContent = () => (
    <Box onClick={handleDrawerToggle} sx={{ width: 250 }}>
      <Typography variant="h6" sx={{ my: 2, px: 2 }}>
        {t('universityManagement')}
      </Typography>
      <Divider />
      <List>
        {filteredNavGroups.map((group) => (
          <React.Fragment key={group.id}>
            {group.title ? (
              <>
                <ListItemButton onClick={() => handleToggleGroup(group.id)}>
                  <ListItemIcon>{group.icon}</ListItemIcon>
                  <ListItemText primary={group.title} />
                  {openGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                
                {/* Collapsible section for group */}
                <Collapse in={openGroups[group.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.map((item) => (
                      <ListItem 
                        key={item.text} 
                        component={RouterLink} 
                        to={item.link}
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'inherit',
                          pl: 4
                        }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Render items directly if no group title
              group.items.map((item) => (
                <ListItem 
                  key={item.text} 
                  component={RouterLink} 
                  to={item.link}
                  sx={{ 
                    textDecoration: 'none', 
                    color: 'inherit' 
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Logo and title */}
            <Box 
              component="img" 
              src="/assets/images/logo.png" 
              alt="University Logo"
              sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                mr: 1, 
                height: 40 
              }}
            />
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              {t('universityManagement')}
            </Typography>

            {/* Mobile title */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              {t('universityManagement')}
            </Typography>

            {/* Desktop navigation */}
            
<Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
  {filteredNavGroups.map((group) => (
    <React.Fragment key={group.id}>
      {group.title ? (
        <NavMenu 
          title={group.title}
          icon={group.icon}
          items={group.items}
        />
      ) : (
        renderMenuItems(group.items)
      )}
    </React.Fragment>
  ))}
</Box>

            {/* Language switcher and user menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LanguageSwitcher />
              
              <Tooltip title={t('userSettings')}>
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 2 }}>
                  <Avatar alt={user?.username}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: direction === 'ltr' ? 'right' : 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: direction === 'ltr' ? 'right' : 'left',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography textAlign="center">{t('logout')}</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Mobile drawer */}
      <Drawer
        anchor={direction === 'ltr' ? 'left' : 'right'}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250 
          },
        }}
      >
        {renderMobileDrawerContent()}
      </Drawer>
    </>
  );
};

export default Navbar;